import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      // Verify token and get user info
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch('/api/admin/verify', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setUser(result.data.user);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('adminToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('Attempting login with:', email);
      
      // Try proxy first, then direct backend URL as fallback
      const urls = [
        '/api/admin/login',
        'http://localhost:5001/api/admin/login'
      ];
      
      let response;
      let lastError;
      
      for (const url of urls) {
        try {
          console.log('Trying login URL:', url);
          response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });
          console.log('Login response status:', response.status);
          break; // Success, exit the loop
        } catch (err) {
          console.warn('Failed login URL:', url, err);
          lastError = err;
          continue; // Try next URL
        }
      }
      
      if (!response) {
        throw lastError || new Error('All login endpoints failed');
      }
      
      if (response.ok) {
        try {
          const result = await response.json();
          console.log('Login successful, received data:', result);
          console.log('Setting user:', result.data.user);
          localStorage.setItem('adminToken', result.data.token);
          setUser(result.data.user);
          console.log('User set, returning true');
          return true;
        } catch (parseError) {
          console.error('Failed to parse login response:', parseError);
          return false;
        }
      } else {
        try {
          const errorData = await response.json();
          console.error('Login failed:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setUser(null);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
